import React, { useState } from 'react';
import { createGeneralInsurance } from '../model/api';
import './GeneralInsuranceForm.css';

const GeneralInsuranceForm = ({ onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        insurance_type: 'car',
        custom_insurance_type: '',
        customer_name: '',
        customer_phone: '',
        customer_email: '',
        vehicle_number: '',
        comments: ''
    });

    const [files, setFiles] = useState({
        rc_document: null,
        existing_insurance: null,
        aadhar: null,
        pan_card: null,
        additional_photos: [] // Array for multiple photos
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const { name, files: selectedFiles } = e.target;
        if (selectedFiles && selectedFiles[0]) {
            if (name === 'additional_photos') {
                // Handle multiple files for additional photos
                setFiles(prev => ({
                    ...prev,
                    [name]: Array.from(selectedFiles)
                }));
            } else {
                // Handle single file for other inputs
                setFiles(prev => ({
                    ...prev,
                    [name]: selectedFiles[0]
                }));
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Validate required fields
            if (!formData.customer_name || !formData.customer_phone) {
                throw new Error('Please fill in all required fields');
            }

            if (formData.insurance_type === 'other' && !formData.custom_insurance_type) {
                throw new Error('Please specify the insurance type');
            }



            // Vehicle number is mandatory for vehicle insurances
            const vehicleTypes = ['car', 'bike', 'public_carrier_vehicle', 'goods_carrier_vehicle', 'three_wheeler'];
            if (vehicleTypes.includes(formData.insurance_type) && !formData.vehicle_number) {
                throw new Error('Vehicle Number is required for this insurance type');
            }

            // File upload logic - NO MANDATORY FILE UPLOAD
            // if (['car', 'bike'].includes(formData.insurance_type) && !files.rc_document) {
            //     throw new Error('RC document is required for car/bike insurance');
            // }

            // Create FormData
            const submitData = new FormData();
            Object.keys(formData).forEach(key => {
                if (key === 'insurance_type') {
                    submitData.append(key, formData.insurance_type === 'other' ? formData.custom_insurance_type : formData.insurance_type);
                } else if (key !== 'custom_insurance_type') {
                    submitData.append(key, formData[key]);
                }
            });

            // Append files
            if (files.rc_document) {
                submitData.append('rc_document', files.rc_document);
            }
            if (files.existing_insurance) {
                submitData.append('existing_insurance', files.existing_insurance);
            }
            if (files.aadhar) {
                submitData.append('aadhar', files.aadhar);
            }
            if (files.pan_card) {
                submitData.append('pan_card', files.pan_card);
            }
            // Append multiple additional photos
            if (files.additional_photos && files.additional_photos.length > 0) {
                files.additional_photos.forEach(photo => {
                    submitData.append('additional_photos', photo);
                });
            }

            const result = await createGeneralInsurance(submitData);

            if (result.ok) {
                alert('Application submitted successfully!');
                if (onSuccess) onSuccess();
            } else {
                throw new Error(result.message || 'Failed to submit application');
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="general-insurance-form">
            <h2>General Insurance Application</h2>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label>Insurance Type *</label>
                    <select
                        name="insurance_type"
                        value={formData.insurance_type}
                        onChange={handleInputChange}
                        required
                    >
                        <option value="car">Car Insurance</option>
                        <option value="bike">Bike Insurance</option>
                        <option value="public_carrier_vehicle">Public Carrier Vehicle</option>
                        <option value="goods_carrier_vehicle">Goods Carrier Vehicle</option>
                        <option value="three_wheeler">Three Wheeler</option>
                        <option value="other">Other Insurance</option>
                    </select>
                </div>

                {formData.insurance_type === 'other' && (
                    <div className="form-group">
                        <label>Specify Insurance Type *</label>
                        <input
                            type="text"
                            name="custom_insurance_type"
                            value={formData.custom_insurance_type}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                )}

                <div className="form-group">
                    <label>Customer Name *</label>
                    <input
                        type="text"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleInputChange}
                        required
                    />
                </div>

                <div className="form-group">
                    <label>Customer Phone *</label>
                    <input
                        type="tel"
                        name="customer_phone"
                        value={formData.customer_phone}
                        onChange={handleInputChange}
                        required
                    />
                </div>

                <div className="form-group">
                    <label>Customer Email</label>
                    <input
                        type="email"
                        name="customer_email"
                        value={formData.customer_email}
                        onChange={handleInputChange}
                    />
                </div>


                {/* Updated Vehicle Logic Check */}
                {['car', 'bike', 'public_carrier_vehicle', 'goods_carrier_vehicle', 'three_wheeler'].includes(formData.insurance_type) && (
                    <div className="form-group">
                        <label>Vehicle Number *</label>
                        <input
                            type="text"
                            name="vehicle_number"
                            value={formData.vehicle_number}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                )}

                {!['car', 'bike', 'public_carrier_vehicle', 'goods_carrier_vehicle', 'three_wheeler'].includes(formData.insurance_type) && (
                    <div className="form-group">
                        <label>Comments / Notes (Optional)</label>
                        <textarea
                            name="comments"
                            value={formData.comments}
                            onChange={handleInputChange}
                            rows="4"
                            placeholder="Please provide details about the insurance requirement..."
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontFamily: 'inherit',
                                fontSize: '14px'
                            }}
                        />
                    </div>
                )}

                <div className="form-section">
                    <h3>Documents (Optional)</h3>

                    {['car', 'bike', 'public_carrier_vehicle', 'goods_carrier_vehicle', 'three_wheeler'].includes(formData.insurance_type) && (
                        <div className="form-group">
                            <label>RC Document (Optional - PDF/Image)</label>
                            <input
                                type="file"
                                name="rc_document"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={handleFileChange}
                            />
                            {files.rc_document && <span className="file-name">{files.rc_document.name}</span>}
                        </div>
                    )}

                    <div className="form-group">
                        <label>Existing Insurance (Optional - PDF/Image)</label>
                        <input
                            type="file"
                            name="existing_insurance"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleFileChange}
                        />
                        {files.existing_insurance && <span className="file-name">{files.existing_insurance.name}</span>}
                    </div>

                    <div className="form-group">
                        <label>Aadhar (Optional - PDF/Image)</label>
                        <input
                            type="file"
                            name="aadhar"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleFileChange}
                        />
                        {files.aadhar && <span className="file-name">{files.aadhar.name}</span>}
                    </div>

                    <div className="form-group">
                        <label>PAN Card (Optional - PDF/Image)</label>
                        <input
                            type="file"
                            name="pan_card"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleFileChange}
                        />
                        {files.pan_card && <span className="file-name">{files.pan_card.name}</span>}
                    </div>

                    <div className="form-group">
                        <label>Additional Photos (Optional - up to 10 images)</label>
                        <input
                            type="file"
                            name="additional_photos"
                            accept=".jpg,.jpeg,.png"
                            onChange={handleFileChange}
                            multiple
                        />
                        {files.additional_photos && files.additional_photos.length > 0 && (
                            <span className="file-name">
                                {files.additional_photos.length} file(s) selected
                            </span>
                        )}
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={onCancel} className="btn-cancel">
                        Cancel
                    </button>
                    <button type="submit" disabled={loading} className="btn-submit">
                        {loading ? 'Submitting...' : 'Submit Application'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default GeneralInsuranceForm;
